/**

 * Build live preview HTML from project files (mirrors backend inline_preview_html).

 * Always inline styles.css + script.js so iframe and new-tab preview are self-contained.

 */



/** Rewrite absolute backend preview URLs to same-origin paths (fixes stale :8001 links). */

export function normalizePreviewUrl(url) {

  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim();

  if (!trimmed) return null;

  if (trimmed.startsWith('/preview-port/')) {
    const m = trimmed.match(/^\/preview-port\/(\d+)/);
    if (m) return trimmed;
  }

  if (trimmed.includes('/vite-preview')) return trimmed;

  if (trimmed.startsWith('/api/')) return trimmed;

  const directPort = trimmed.match(/^https?:\/\/127\.0\.0\.1:(\d+)(\/.*)?$/);

  if (directPort) return `/preview-port/${directPort[1]}${directPort[2] || '/'}`;

  const localhostPort = trimmed.match(/^https?:\/\/localhost:(\d+)(\/.*)?$/);

  if (localhostPort) return `/preview-port/${localhostPort[1]}${localhostPort[2] || '/'}`;

  try {

    const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

    if (parsed.hostname === '127.0.0.1' && parsed.port) {

      return `/preview-port/${parsed.port}${parsed.pathname === '/' ? '/' : parsed.pathname}${parsed.search}`;

    }

    if (parsed.hostname === 'localhost' && parsed.port) {

      return `/preview-port/${parsed.port}${parsed.pathname === '/' ? '/' : parsed.pathname}${parsed.search}`;

    }

    if (parsed.pathname.includes('/preview-port/')) {

      return `${parsed.pathname}${parsed.search}`;

    }

    if (parsed.pathname.includes('/vite-preview')) {

      return `${parsed.pathname}${parsed.search}`;

    }

    // Rewrite stale direct port URLs to same-origin proxy
    const portParam = parsed.searchParams.get('port');

    if (portParam && /^\d+$/.test(portParam)) {

      return `/preview-port/${portParam}/`;

    }

  } catch {

    /* ignore */

  }

  return trimmed;

}

/** CRA/Emergent dev-server port (same host as /preview-port proxy). */
export function devServerPort() {
  if (typeof window !== 'undefined' && window.location.port) {
    return parseInt(window.location.port, 10);
  }
  return parseInt(process.env.PORT || '3000', 10);
}

/** Extract the target TCP port from a preview-port or localhost URL. */
export function previewPortFromUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const rel = trimmed.match(/\/preview-port\/(\d+)/);
  if (rel) return parseInt(rel[1], 10);
  try {
    const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const pathMatch = parsed.pathname.match(/\/preview-port\/(\d+)/);
    if (pathMatch) return parseInt(pathMatch[1], 10);
    if ((parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') && parsed.port) {
      return parseInt(parsed.port, 10);
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** True when a preview-port URL would proxy back to this dev server (circular). */
export function isUnsafePreviewPortUrl(url) {
  const port = previewPortFromUrl(url);
  if (!port) return false;
  return port === devServerPort();
}

/** GPT Pilot static preview served by the backend after vite build. */
export function gptPilotPreviewUrl(jobId, apiBase = '') {
  if (!jobId) return null;
  return `${apiBase}/api/gpt-pilot/preview/${encodeURIComponent(jobId)}/`;
}

/** GPT Pilot Vite dev-server preview (fast path — no production build). */
export function gptPilotDevPreviewUrl(jobId, apiBase = '') {
  if (!jobId) return null;
  return `${apiBase}/api/gpt-pilot/preview-dev/${encodeURIComponent(jobId)}/`;
}

export function isViteReactScaffold(html) {

  if (!html || typeof html !== 'string') return false;

  return html.includes('id="root"') && /\/src\/main\.(tsx|jsx)/i.test(html);

}



export function isAppShellHtml(html) {

  if (!html || typeof html !== 'string') return false;

  // The host Snagcart SPA shell (CRA build) is never a generated preview. It
  // gets returned when a preview fetch accidentally resolves against the
  // frontend origin (e.g. relative URL on a split frontend/backend deploy),
  // and rendering it in the iframe shows a blank app + react-router
  // "No routes matched location srcdoc".

  if (html.toLowerCase().includes('a product of emergent.sh')) return true;

  if (html.includes('id="root"') && /\/static\/js\/main\.[a-z0-9]+\.js/i.test(html)) return true;

  return false;

}



export function isScaffoldIndexHtml(indexHtml) {

  if (!indexHtml || typeof indexHtml !== 'string') return false;

  if (isViteReactScaffold(indexHtml)) return true;

  return (

    /<main id="app">\s*<\/main>/i.test(indexHtml)

    && !indexHtml.includes('SB-SECTION')

    && !indexHtml.includes('sb-hero')

  );

}



export function isCatalogHtml(html) {

  if (!html || typeof html !== 'string') return false;

  return html.includes('SB-SECTION') && html.includes('sb-nav');

}



export function isLegacyLlmHtml(html) {

  if (!html || typeof html !== 'string') return false;

  return /<nav[^>]*>\s*<ul>/i.test(html) && !isCatalogHtml(html);

}



export function buildPreviewFromFiles(files) {

  if (!files || typeof files !== 'object') return '';

  const preInlined = files['preview.html'] || '';

  if (preInlined && preInlined.includes('<style')) {

    return preInlined;

  }



  const indexHtml = files['index.html'] || '';

  const stylesCss = files['styles.css'] || '';

  const scriptJs = files['script.js'] || '';

  if (!indexHtml) return '';



  let output = indexHtml;

  if (stylesCss) {

    const linked = output.replace(

      /<link[^>]+href=["']\.?\/?styles\.css["'][^>]*\/?>/gi,

      `<style>${stylesCss}</style>`,

    );

    if (linked !== output) {

      output = linked;

    } else if (output.includes('</head>') && !output.includes(stylesCss.slice(0, 40))) {

      output = output.replace('</head>', `<style>${stylesCss}</style>\n</head>`);

    } else if (/<body[^>]*>/i.test(output) && !output.includes(stylesCss.slice(0, 40))) {

      output = output.replace(/<body([^>]*)>/i, `<body$1><style>${stylesCss}</style>`);

    }

  }

  if (scriptJs) {

    output = output.replace(/<script[^>]+src=["']\.?\/?script\.js["'][^>]*>\s*<\/script>/gi, '');

    const bodyClose = output.toLowerCase().lastIndexOf('</body>');

    const hasInline = bodyClose !== -1 && output.slice(bodyClose - 400, bodyClose).includes('<script>');

    if (output.includes('</body>') && !hasInline) {

      output = output.replace('</body>', `<script>${scriptJs}</script></body>`);

    }

  }

  return output;

}



function isRouterShellApp(code) {
  if (!code) return false;
  const hasRouter = /\b(?:HashRouter|BrowserRouter)\b/.test(code);
  const hasRoutes = /\bRoutes\b/.test(code) && /\bRoute\b/.test(code);
  return (hasRouter && hasRoutes) || code.includes('QueryClientProvider');
}

function isGenericScaffoldSource(code) {
  if (!code) return false;
  const low = code.toLowerCase();
  return [
    'welcome to snagcart',
    'start building your amazing project here',
    'made-with-openlovable',
    'made-with-snagcart',
    'content is just a fallback if you fail to update the page',
    'hello world',
    'hello {data}',
  ].some((marker) => low.includes(marker));
}

function isUtilityPreviewPage(path, content = '') {
  const norm = (path || '').replace(/\\/g, '/');
  const base = (norm.split('/').pop() || '').replace(/\.(tsx|jsx)$/, '');
  if (/^(NotFound|404|ErrorPage|Error)$/i.test(base)) return true;
  if (/404 Error:/i.test(content)) return true;
  return false;
}

function pickPreviewAppContent(files) {
  const apps = [];
  for (const [path, content] of Object.entries(files || {})) {
    const norm = path.replace(/\\/g, '/');
    if (/^src\/App\.(tsx|jsx)$/.test(norm)) apps.push([path, content]);
  }
  apps.sort(([a], [b]) => a.localeCompare(b));
  if (!apps.length) return { path: '', content: '' };
  const landing = apps.find(([, content]) => !isRouterShellApp(content) && !isGenericScaffoldSource(content));
  if (landing) return { path: landing[0], content: landing[1] };
  const nonRouter = apps.find(([, content]) => !isRouterShellApp(content));
  if (nonRouter) return { path: nonRouter[0], content: nonRouter[1] };
  return { path: apps[0][0], content: apps[0][1] };
}

function prunePreviewComponentFiles(componentFiles, { indexContent = '', indexFromPage = false } = {}) {
  const pruned = { ...(componentFiles || {}) };
  for (const [path, content] of Object.entries(componentFiles || {})) {
    const norm = path.replace(/\\/g, '/');
    if (/pages\/Index\.(tsx|jsx)$/i.test(norm) && isGenericScaffoldSource(content)) {
      delete pruned[path];
      continue;
    }
    if (isUtilityPreviewPage(path, content) && indexContent && !isGenericScaffoldSource(indexContent)) {
      delete pruned[path];
      continue;
    }
    if (/\/App\.(tsx|jsx)$/i.test(norm) && isRouterShellApp(content) && indexContent && !isRouterShellApp(indexContent)) {
      delete pruned[path];
    }
  }
  return pruned;
}

export function isSandpackDefaultApp(code) {
  if (!code || typeof code !== 'string') return true;
  const trimmed = code.trim();
  if (isGenericScaffoldSource(trimmed)) return true;
  return /<h1>\s*Hello\s*(\{data\}|world)\s*<\/h1>/i.test(trimmed);
}

function stripDestructuredParamTypes(code) {
  if (!code) return '';
  const out = [];
  let i = 0;
  const n = code.length;
  while (i < n) {
    if (i + 1 < n && code[i] === '}' && code[i + 1] === ':') {
      let j = i + 2;
      while (j < n && ' \t\n\r'.includes(code[j])) j += 1;
      if (j < n && code[j] === '{') {
        let depth = 0;
        let k = j;
        let matched = false;
        while (k < n) {
          const ch = code[k];
          if (ch === '{') depth += 1;
          else if (ch === '}') {
            depth -= 1;
            if (depth === 0) {
              let m = k + 1;
              while (m < n && ' \t\n\r'.includes(code[m])) m += 1;
              if (m + 1 < n && code.slice(m, m + 2) === '=>') {
                out.push('}');
                i = m;
                matched = true;
              } else {
                let end = m;
                if (end < n && code[end] === ')') {
                  end += 1;
                  while (end < n && ' \t\n\r'.includes(code[end])) end += 1;
                }
                if (end + 1 <= n && code.slice(end, end + 2) === '=>') {
                  out.push('}');
                  if (m < n && code[m] === ')') out.push(')');
                  i = end;
                  matched = true;
                }
              }
              if (matched) break;
            }
          }
          k += 1;
        }
        if (matched) continue;
      }
    }
    out.push(code[i]);
    i += 1;
  }
  return out.join('');
}

function stripTs(code) {
  if (!code) return '';
  let c = code;
  c = c.replace(/^["']use client["'];?\s*\n?/gm, '');
  c = c.replace(/import\s+type\s+.*?;\n?/g, '');
  c = c.replace(/:\s*React\.\w+(?:<[^>]*>)?/g, '');
  c = c.replace(/:\s*(?:string|number|boolean|any|void|null|undefined)(?:\[\])?/g, '');
  c = c.replace(/^(?:export\s+)?(?:interface|type)\s+\w+[^{]*\{[^}]*\}\s*;?\n?/gm, '');
  // Only strip hook/util generics — never bare <Tag> which matches JSX (<Routes>, <Route>).
  c = c.replace(
    /\b(useState|useRef|useMemo|useCallback|useReducer|useContext|Promise|Array|Record|Map|Set|ReadonlyArray)<[^>]+>/g,
    '$1',
  );
  c = c.replace(/\s+as\s+\w+/g, '');
  c = stripDestructuredParamTypes(c);
  return c;
}

function stripImports(code) {
  return (code || '').split('\n').filter((line) => !line.trim().startsWith('import ')).join('\n');
}

function stripExports(code) {
  if (!code) return '';
  let c = code;
  c = c.replace(/^export\s+default\s+/gm, '');
  c = c.replace(/^export\s+(?=(?:async\s+)?(?:function|class|const|let|var)\s)/gm, '');
  return c;
}

function sanitizeInvalidIdentifiers(code) {
  if (!code) return '';
  return code.replace(
    /\b(const|let|var|function)\s+([A-Za-z_$][\w$]*(?:-[A-Za-z][\w$]*)+)/g,
    (_, kind, name) => `${kind} ${name.replace(/-/g, '')}`,
  );
}

function isLibraryPackPreviewPath(path) {
  const norm = (path || '').replace(/\\/g, '/').toLowerCase();
  if (norm.includes('/magicui/') || norm.endsWith('/magicui')) return true;
  if (norm.includes('/aceternity/') || norm.endsWith('/aceternity')) return true;
  if (norm.includes('/ui/') || norm.endsWith('/ui')) return true;
  const base = norm.split('/').pop() || '';
  return base.startsWith('made-with-');
}

function shouldSkipPreviewComponent(path) {
  const norm = (path || '').replace(/\\/g, '/');
  const base = norm.split('/').pop()?.toLowerCase() || '';
  if (['main.tsx', 'main.jsx', 'app.tsx', 'app.jsx'].includes(base)) return true;
  if (isLibraryPackPreviewPath(path)) return true;
  return false;
}

function trackPreviewComponentFile(componentFiles, path, content) {
  if (shouldSkipPreviewComponent(path)) return;
  componentFiles[path] = content;
}

function sanitizeBrokenJsxAttributes(code) {
  if (!code || typeof code !== 'string') return code;
  let next = code;
  // <Hero headline, subheadline, ctaLabel /> -> <Hero />
  next = next.replace(
    /<([A-Z][A-Za-z0-9_]*)\s+([A-Za-z_$][\w$]*(?:\s*,\s*[A-Za-z_$][\w$]*)+)\s*(?:\/>|>)/g,
    '<$1 />',
  );
  // <Component {foo, bar} /> or broken spreads -> <Component />
  next = next.replace(
    /<([A-Z][A-Za-z0-9_]*)\s*\{\s*[^}]+\s*,\s*[^}]+\s*\}\s*(?:\/>|>)/g,
    '<$1 />',
  );
  return next;
}

function rewriteLucideIconCollisions(code, reserved) {
  const collisions = [...extractLucideImports(code)].filter((name) => reserved.has(name));
  if (!collisions.length) return code;
  let next = code;
  collisions.sort((a, b) => b.length - a.length).forEach((name) => {
    const alias = `__Lucide${name}`;
    next = next.replace(new RegExp(`<${name}(\\s|/)`, 'g'), `<${alias}$1`);
  });
  return next;
}

function preparePreviewSource(code, reserved = null) {
  const withLucideFix = reserved ? rewriteLucideIconCollisions(code, reserved) : code;
  return sanitizeBrokenJsxAttributes(
    sanitizeInvalidIdentifiers(stripTs(stripExports(stripImports(withLucideFix)))),
  );
}

function componentNameFromPath(path) {
  const base = (path || '').replace(/\\/g, '/').split('/').pop() || '';
  return base.replace(/\.(tsx|jsx|ts|js)$/i, '');
}

function previewComponentSymbol(path) {
  const base = componentNameFromPath(path);
  if (/^[A-Z][A-Za-z0-9_]*$/.test(base)) return base;
  return base
    .split(/[-_]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('') || 'PreviewComponent';
}

function wrapPreviewComponentIife(code, exportName) {
  const name = (exportName || '').trim();
  const body = (code || '').trim();
  if (!name || !body) return code;
  return `var ${name} = (function() {\n${body}\nreturn typeof ${name} !== 'undefined' ? ${name} : undefined;\n})();`;
}

function collectPreviewAnchorHrefs(files) {
  const anchors = new Set();
  for (const content of Object.values(files || {})) {
    if (!content) continue;
    const re = /href=["']#([a-zA-Z][\w-]*)/g;
    let match = re.exec(content);
    while (match) {
      anchors.add(match[1].toLowerCase());
      match = re.exec(content);
    }
  }
  return anchors;
}

function collectDefinedDomIds(files) {
  const ids = new Set();
  for (const content of Object.values(files || {})) {
    if (!content) continue;
    const re = /\bid=["']([^"']+)["']/g;
    let match = re.exec(content);
    while (match) {
      ids.add(match[1].toLowerCase());
      match = re.exec(content);
    }
  }
  return ids;
}

function hasPageComponentForAnchor(files, anchor) {
  const low = anchor.toLowerCase();
  for (const path of Object.keys(files || {})) {
    const norm = path.replace(/\\/g, '/');
    const base = componentNameFromPath(norm).toLowerCase();
    if (base === low || base === `${low}page`) return true;
    if (norm.toLowerCase().includes(`/pages/${low}`)) return true;
  }
  return false;
}

function missingPreviewAnchors(files) {
  const wanted = collectPreviewAnchorHrefs(files);
  if (!wanted.size) return [];
  const defined = collectDefinedDomIds(files);
  const componentNames = new Set(
    Object.keys(files || {})
      .filter((p) => /\.(tsx|jsx|ts|js)$/.test(p))
      .map((p) => componentNameFromPath(p).toLowerCase()),
  );
  return [...wanted].filter((anchor) => {
    const low = anchor.toLowerCase();
    if (defined.has(low)) return false;
    if (componentNames.has(low)) return false;
    if (hasPageComponentForAnchor(files, low)) return false;
    return true;
  }).sort();
}

function anchorFnName(anchor) {
  return anchor.replace(/[^\w]/g, '_');
}

function previewAnchorSectionJs(anchor) {
  const fn = anchorFnName(anchor);
  const low = anchor.toLowerCase();
  if (low === 'login' || low === 'signin' || low === 'sign-in') {
    return `
function __PreviewAnchor_${fn}() {
  const msgState = useState("");
  const setMsg = msgState[1];
  return (
    <section id="${anchor}" className="py-24 px-6 bg-slate-50 scroll-mt-24">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader><CardTitle>Login</CardTitle><CardDescription>Sign in to your account</CardDescription></CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={function(e) { e.preventDefault(); setMsg("Signed in (preview). Use Live Preview for real authentication."); }}>
              <div className="space-y-2"><Label htmlFor="preview-login-email">Email</Label><Input id="preview-login-email" type="email" name="email" placeholder="you@example.com" required /></div>
              <div className="space-y-2"><Label htmlFor="preview-login-password">Password</Label><Input id="preview-login-password" type="password" name="password" placeholder="••••••••" required /></div>
              <Button type="submit" className="w-full">Sign in</Button>
              {msgState[0] ? <p className="text-sm text-emerald-600 mt-2">{msgState[0]}</p> : null}
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}`;
  }
  if (low === 'register' || low === 'signup' || low === 'sign-up') {
    return `
function __PreviewAnchor_${fn}() {
  const msgState = useState("");
  const setMsg = msgState[1];
  return (
    <section id="${anchor}" className="py-24 px-6 bg-slate-50 scroll-mt-24">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader><CardTitle>Create account</CardTitle><CardDescription>Register to get started</CardDescription></CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={function(e) { e.preventDefault(); setMsg("Account created (preview). Use Live Preview for real sign-up."); }}>
              <div className="space-y-2"><Label>Name</Label><Input type="text" name="name" placeholder="Your name" required /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" name="email" placeholder="you@example.com" required /></div>
              <div className="space-y-2"><Label>Password</Label><Input type="password" name="password" placeholder="••••••••" required /></div>
              <Button type="submit" className="w-full">Register</Button>
              {msgState[0] ? <p className="text-sm text-emerald-600 mt-2">{msgState[0]}</p> : null}
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}`;
  }
  if (low === 'contact') {
    return `
function __PreviewAnchor_${fn}() {
  const msgState = useState("");
  const setMsg = msgState[1];
  return (
    <section id="${anchor}" className="py-24 px-6 bg-slate-50 scroll-mt-24">
      <div className="max-w-5xl mx-auto grid gap-10 lg:grid-cols-2 items-start">
        <div className="space-y-6">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-sky-600">Get in touch</p>
            <h2 className="text-3xl font-bold mt-2">Contact information</h2>
            <p className="text-zinc-500 mt-2">Reach us by phone, email, or send a message using the form.</p>
          </div>
          <div className="space-y-4 text-sm">
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <p className="font-medium">Phone</p>
              <p className="text-zinc-500 mt-1">+1 (555) 123-4567</p>
            </div>
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <p className="font-medium">Email</p>
              <p className="text-zinc-500 mt-1">hello@example.com</p>
            </div>
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <p className="font-medium">Address</p>
              <p className="text-zinc-500 mt-1">123 Main Street, Suite 100<br />Your City, ST 12345</p>
            </div>
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Send a message</CardTitle>
            <CardDescription>We typically respond within one business day.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={function(e) { e.preventDefault(); setMsg("Message sent (preview). Use Live Preview to wire a real contact API."); }}>
              <div className="space-y-2"><Label htmlFor="preview-contact-name">Name</Label><Input id="preview-contact-name" type="text" name="name" placeholder="Your name" required /></div>
              <div className="space-y-2"><Label htmlFor="preview-contact-email">Email</Label><Input id="preview-contact-email" type="email" name="email" placeholder="you@example.com" required /></div>
              <div className="space-y-2"><Label htmlFor="preview-contact-message">Message</Label><Textarea id="preview-contact-message" name="message" placeholder="How can we help?" rows={4} required /></div>
              <Button type="submit" className="w-full">Send message</Button>
              {msgState[0] ? <p className="text-sm text-emerald-600 mt-2">{msgState[0]}</p> : null}
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}`;
  }
  const title = anchor.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return `
function __PreviewAnchor_${fn}() {
  return (
    <section id="${anchor}" className="py-24 px-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-4">${title}</h2>
        <p className="text-zinc-500">Explore this section of the site.</p>
      </div>
    </section>
  );
}`;
}

function buildMissingAnchorPreviewJs(files) {
  const missing = missingPreviewAnchors(files);
  if (!missing.length) return { sectionsJs: '', renderBlock: '<AppComponent />' };
  const sectionsJs = `${missing.map((a) => previewAnchorSectionJs(a)).join('\n')}
function __PreviewMissingAnchors() {
  return (<>{${missing.map((a) => `<__PreviewAnchor_${anchorFnName(a)} />`).join(' ')}}</>);
}`;
  return { sectionsJs, renderBlock: '<><AppComponent /><__PreviewMissingAnchors /></>' };
}

function injectSectionIdForAnchor(componentBody, path, wantedAnchors) {
  const name = componentNameFromPath(path).toLowerCase();
  if (!wantedAnchors.has(name)) return componentBody;
  if (new RegExp(`\\bid=["']${name}["']`).test(componentBody)) return componentBody;
  let updated = componentBody.replace(/(<section)(\s)/, `$1 id="${name}"$2`);
  if (updated !== componentBody) return updated;
  updated = componentBody.replace(/(return\s*\(\s*<section)(\s)/, `$1 id="${name}"$2`);
  if (updated !== componentBody) return updated;
  return componentBody.replace(/(return\s*\(\s*<(?:div|main))(\s)/, `$1 id="${name}"$2`);
}

const PREVIEW_ANCHOR_NAV_BOOTSTRAP = `
function __scrollPreviewAnchor(raw) {
  if (!raw) return;
  var el = document.getElementById(raw);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  try { window.location.hash = raw; } catch (_) {}
}
setTimeout(function() {
  var initial = (window.location.hash || '').replace(/^#/, '').split('?')[0];
  if (initial) __scrollPreviewAnchor(initial);
  document.addEventListener('click', function(e) {
    var a = e.target && e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var raw = (a.getAttribute('href') || '').slice(1).split('?')[0];
    if (!raw || raw === '/') return;
    if (!document.getElementById(raw)) return;
    e.preventDefault();
    __scrollPreviewAnchor(raw);
  }, true);
  window.addEventListener('hashchange', function() {
    var hh = (window.location.hash || '').replace(/^#/, '').split('?')[0];
    if (hh) __scrollPreviewAnchor(hh);
  });
}, 400);
`;

const REACT_PREVIEW_PROVIDER_STUBS = `
// Stub react-query + tooltip/toast providers; functional hash router for multi-page instant preview
const QueryClient = function() { this.defaultOptions = {}; };
const QueryClientProvider = ({children, client}) => <>{children}</>;
const TooltipProvider = ({children}) => <>{children}</>;
const Toaster = () => null;
const Sonner = () => null;
const useState = React.useState;
const useEffect = React.useEffect;
const useMemo = React.useMemo;
const useRef = React.useRef;
const useCallback = React.useCallback;
const createContext = React.createContext;
const useContext = React.useContext;
const Fragment = React.Fragment;
const useQuery = () => ({ data: undefined, isLoading: false, error: null });
const useMutation = () => ({ mutate: () => {}, isLoading: false });

const __RouterCtx = createContext({ path: "/", navigate: function() {} });
function __normalizePreviewPath(to) {
  if (!to) return "/";
  var s = String(to);
  if (s.charAt(0) === "#") s = s.slice(1);
  if (s.charAt(0) !== "/") s = "/" + s;
  var q = s.indexOf("?");
  if (q >= 0) s = s.slice(0, q);
  return s || "/";
}
function __readHashPath() {
  var h = (typeof window !== "undefined" && window.location.hash) || "";
  if (h.charAt(0) === "#") h = h.slice(1);
  if (!h || h === "/") return "/";
  return h.charAt(0) === "/" ? __normalizePreviewPath(h) : __normalizePreviewPath("/" + h);
}
function __matchPreviewRoute(routePath, currentPath) {
  var rp = routePath == null || routePath === "" ? "/" : __normalizePreviewPath(routePath);
  var cp = __normalizePreviewPath(currentPath);
  if (rp === cp) return true;
  if (rp !== "/" && cp === rp.replace(/\\/$/, "")) return true;
  if (cp !== "/" && rp === cp.replace(/\\/$/, "")) return true;
  if (rp.slice(-2) === "/*") {
    var base = rp.slice(0, -2);
    return cp === base || cp.indexOf(base + "/") === 0;
  }
  var rpParts = rp.split("/").filter(Boolean);
  var cpParts = cp.split("/").filter(Boolean);
  if (rpParts.length !== cpParts.length) return false;
  for (var i = 0; i < rpParts.length; i++) {
    if (rpParts[i].charAt(0) === ":") continue;
    if (rpParts[i] !== cpParts[i]) return false;
  }
  return true;
}
function __HashRouterInner({ children }) {
  const pathState = useState(__readHashPath);
  const path = pathState[0], setPath = pathState[1];
  const navigate = useCallback(function(to, opts) {
    var p = __normalizePreviewPath(to);
    if (typeof window !== "undefined") window.location.hash = p;
    setPath(p);
  }, []);
  useEffect(function() {
    var onHash = function() { setPath(__readHashPath()); };
    window.addEventListener("hashchange", onHash);
    if (__readHashPath() !== path) setPath(__readHashPath());
    return function() { window.removeEventListener("hashchange", onHash); };
  }, []);
  return React.createElement(__RouterCtx.Provider, { value: { path: path, navigate: navigate } }, children);
}
const HashRouter = ({ children }) => React.createElement(__HashRouterInner, null, children);
const BrowserRouter = HashRouter;
const Route = function Route() { return null; };
const Routes = ({ children }) => {
  const ctx = useContext(__RouterCtx);
  const path = ctx.path;
  const kids = React.Children.toArray(children);
  const isRouteEl = function(child) {
    if (!child || !child.props) return false;
    return child.props.path != null || child.props.index === true;
  };
  for (var i = 0; i < kids.length; i++) {
    var child = kids[i];
    if (!isRouteEl(child)) continue;
    if (child.props.index && (path === "/" || path === "")) {
      return child.props.element || child.props.children || null;
    }
    if (child.props.path != null && __matchPreviewRoute(child.props.path, path)) {
      return child.props.element || child.props.children || null;
    }
  }
  for (var j = 0; j < kids.length; j++) {
    var idxChild = kids[j];
    if (!isRouteEl(idxChild)) continue;
    if ((idxChild.props.index || idxChild.props.path === "/" || idxChild.props.path == null) && (path === "/" || path === "")) {
      return idxChild.props.element || idxChild.props.children || null;
    }
  }
  return null;
};
const Outlet = () => null;
const Navigate = ({ to, replace }) => {
  const ctx = useContext(__RouterCtx);
  useEffect(function() { ctx.navigate(to, { replace: replace }); }, [to, replace, ctx.navigate]);
  return null;
};
const useLocation = () => {
  const ctx = useContext(__RouterCtx);
  return { pathname: ctx.path, search: "", hash: "", state: null };
};
const useNavigate = () => useContext(__RouterCtx).navigate;
const Link = ({ children, to, className = "", onClick, ...props }) => {
  const ctx = useContext(__RouterCtx);
  var href = "#" + __normalizePreviewPath(to);
  return (
    <a href={href} className={className} onClick={function(e) {
      if (onClick) onClick(e);
      if (e.defaultPrevented) return;
      e.preventDefault();
      ctx.navigate(to);
    }} {...props}>{children}</a>
  );
};
const NavLink = ({ children, to, className = "", ...props }) => {
  const ctx = useContext(__RouterCtx);
  const active = __matchPreviewRoute(to, ctx.path);
  const merged = typeof className === "function" ? className({ isActive: active }) : className;
  return <Link to={to} className={merged} {...props}>{typeof children === "function" ? children({ isActive: active }) : children}</Link>;
};
const useParams = () => ({});
const useSearchParams = () => [{}, function() {}];
`;

const FRAMER_MOTION_STUB = `
const __MOTION_OMIT = ["initial","animate","exit","whileInView","whileHover","whileTap","whileFocus","whileDrag","drag","dragConstraints","dragElastic","variants","transition","viewport","custom","layout","layoutId","layoutScroll","onViewportEnter","onViewportLeave","onAnimationComplete","onAnimationStart","transformTemplate","transformValues","inherit"];
function __makeMotion(Tag) {
  return React.forwardRef(function MotionComp(props, ref) {
    const clean = {};
    for (const k in props) { if (__MOTION_OMIT.indexOf(k) === -1) clean[k] = props[k]; }
    clean.ref = ref;
    return React.createElement(typeof Tag === "string" ? Tag : Tag, clean);
  });
}
const __motionCache = {};
const motion = new Proxy(function() {}, {
  get: function(_t, tag) {
    if (typeof tag !== "string") return undefined;
    if (!__motionCache[tag]) __motionCache[tag] = __makeMotion(tag);
    return __motionCache[tag];
  },
  apply: function(_t, _this, args) { return __makeMotion(args[0] || "div"); }
});
const AnimatePresence = function(props) { return React.createElement(React.Fragment, null, props.children); };
const useScroll = function() { return { scrollY: { get: function(){return 0;}, onChange: function(){return function(){};} }, scrollYProgress: { get: function(){return 0;}, onChange: function(){return function(){};} } }; };
const useTransform = function() { return 0; };
const useMotionValue = function(v) { return { get: function(){return v;}, set: function(){}, onChange: function(){return function(){};} }; };
const useSpring = function(v) { return { get: function(){return v;}, set: function(){} }; };
const useInView = function() { return true; };
const useAnimation = function() { return { start: function(){return Promise.resolve();}, stop: function(){}, set: function(){} }; };
const useReducedMotion = function() { return false; };
`;

const PREVIEW_LUCIDE_NAMES = [
  'ArrowRight', 'Star', 'Zap', 'Heart', 'Shield', 'Globe', 'Users', 'Mail', 'Phone',
  'MapPin', 'Clock', 'Calendar', 'Check', 'ChevronRight', 'Menu', 'X', 'Play',
  'Sparkles', 'Rocket', 'Target', 'TrendingUp', 'Award', 'Coffee', 'Code',
  'Dumbbell', 'Activity', 'Flame', 'Timer', 'Trophy', 'BarChart3', 'Brain',
  'Lightbulb', 'Palette', 'Camera', 'Music', 'Book', 'Home', 'Settings', 'Search',
  'MessageCircle', 'Send', 'Download',
];

function collectReservedPreviewNames(componentFiles, indexFromPage) {
  const reserved = new Set();
  for (const path of Object.keys(componentFiles || {})) {
    if (shouldSkipPreviewComponent(path)) continue;
    const norm = path.replace(/\\/g, '/');
    if (indexFromPage && norm.includes('/pages/')) continue;
    reserved.add(componentNameFromPath(path));
  }
  return reserved;
}

function extractLucideImports(code) {
  const names = new Set();
  if (!code) return names;
  const re = /import\s+\{([^}]+)\}\s*from\s*['"]lucide-react['"]/g;
  let match = re.exec(code);
  while (match) {
    match[1].split(',').forEach((part) => {
      const token = part.trim();
      if (!token) return;
      const name = token.includes(' as ') ? token.split(' as ').pop().trim() : token;
      if (/^[A-Za-z_]\w*$/.test(name)) names.add(name);
    });
    match = re.exec(code);
  }
  return names;
}

function collectLucideIconsNeeded(componentFiles, indexContent, indexFromPage) {
  const needed = new Set(PREVIEW_LUCIDE_NAMES);
  const sources = [indexContent];
  for (const [path, content] of Object.entries(componentFiles || {})) {
    if (shouldSkipPreviewComponent(path)) continue;
    const norm = path.replace(/\\/g, '/');
    if (indexFromPage && norm.includes('/pages/')) continue;
    sources.push(content);
  }
  sources.forEach((src) => {
    extractLucideImports(src).forEach((n) => needed.add(n));
  });
  return needed;
}

const KNOWN_PREVIEW_GLOBALS = new Set([
  'Button', 'Card', 'CardHeader', 'CardContent', 'CardTitle', 'CardDescription',
  'CardFooter', 'Badge', 'Input', 'Textarea', 'Separator', 'Avatar', 'Tabs',
  'TabsList', 'TabsTrigger', 'TabsContent', 'NavigationMenu', 'Dialog', 'Label',
  'Accordion', 'AccordionItem', 'AccordionTrigger', 'AccordionContent',
  'AnimatePresence', 'QueryClient', 'QueryClientProvider', 'TooltipProvider',
  'Toaster', 'Sonner', 'HashRouter', 'BrowserRouter', 'Routes', 'Route', 'Outlet',
  'Navigate', 'Link', 'NavLink', 'LucideIcon', 'CheckIcon', 'Index', 'App', 'Fragment',
  'React', 'ReactDOM', 'Math', 'Object', 'Array', 'Image', 'Infinity', 'JSON',
  'Boolean', 'Number', 'String', 'Date', 'Promise', 'Map', 'Set',
]);

function autoPassthroughStubs(sources, defined) {
  const tags = new Set();
  for (const src of sources) {
    if (!src) continue;
    const re = /<([A-Z][A-Za-z0-9_]*)/g;
    let match = re.exec(src);
    while (match) {
      tags.add(match[1]);
      match = re.exec(src);
    }
  }
  const unknown = [...tags].filter((t) => !defined.has(t)).sort();
  return unknown.map(
    (t) => `const ${t} = ({children, className = "", ...props}) => React.createElement("div", {className, ...props}, children);`,
  ).join('\n');
}

function buildLucideStubs(reserved, needed) {
  const iconNames = [...(needed || PREVIEW_LUCIDE_NAMES)].sort();
  return iconNames
    .map((name) => {
      if (reserved.has(name)) {
        return `const __Lucide${name} = LucideIcon;`;
      }
      return name === 'Check' ? 'const Check = CheckIcon;' : `const ${name} = LucideIcon;`;
    })
    .join('\n');
}



export function hasReactWorkspace(files) {
  if (!files || typeof files !== 'object') return false;
  return Object.keys(files).some((p) => p.includes('src/') && /\.(tsx|jsx)$/.test(p));
}

/** True when Sandpack live preview should replace iframe srcdoc. */
export function shouldUseSandpack(files) {
  if (!files || typeof files !== 'object') return false;
  const paths = Object.keys(files);
  if (!paths.length) return false;

  const pkg = files['package.json'] || '';
  const hasReactDep = /"react"\s*:/.test(pkg);
  const hasVite = /"vite"\s*:/.test(pkg) || paths.some((p) => /vite\.config\.(ts|js|mjs)/i.test(p));
  const hasTsx = paths.some((p) => /\.(tsx|jsx)$/.test(p) && p.includes('src/'));

  if ((hasReactDep || hasTsx) && (hasVite || hasTsx)) return true;
  if (hasReactWorkspace(files) && paths.length >= 3) return true;
  return false;
}

/** Pick Sandpack starter template from file tree. */
export function resolveSandpackTemplate(files) {
  if (!files || typeof files !== 'object') return 'react';
  const paths = Object.keys(files);
  const pkg = files['package.json'] || '';
  const hasVite = /"vite"\s*:/.test(pkg) || paths.some((p) => /vite\.config\.(ts|js|mjs)/i.test(p));
  const hasTs = paths.some((p) => /\.tsx?$/.test(p));
  if (hasVite && hasTs) return 'react-ts';
  if (hasTs) return 'react-ts';
  return 'react';
}

export function resolveSandpackEntry(files) {
  if (!files || typeof files !== 'object') return null;
  const normalized = Object.keys(files).map((p) => p.replace(/\\/g, '/').replace(/^\//, ''));
  const priority = ['src/main.tsx', 'src/main.jsx', 'src/index.tsx', 'src/index.jsx'];
  for (const candidate of priority) {
    if (normalized.includes(candidate)) return `/${candidate}`;
  }
  return null;
}

function findNonScaffoldPreviewCandidates(files) {
  return Object.entries(files || {})
    .filter(([path, content]) => {
      if (!content) return false;
      const norm = path.replace(/\\/g, '/');
      if (!/\.(tsx|jsx)$/.test(norm) || !norm.includes('src/')) return false;
      if (shouldSkipPreviewComponent(path)) return false;
      if (isRouterShellApp(content)) return false;
      if (isGenericScaffoldSource(content)) return false;
      return true;
    })
    .sort((a, b) => {
      const score = (path) => {
        const norm = path.replace(/\\/g, '/');
        if (/pages\/Index\.(tsx|jsx)$/i.test(norm)) return 0;
        if (norm.includes('/pages/')) return 1;
        if (norm.includes('/components/')) return 2;
        return 3;
      };
      const diff = score(a[0]) - score(b[0]);
      if (diff !== 0) return diff;
      return b[1].length - a[1].length;
    });
}

function landingSectionRank(symbol) {
  const order = [
    ['navbar', 0], ['header', 1], ['hero', 2], ['stats', 3], ['feature', 4],
    ['testimonial', 5], ['pricing', 6], ['faq', 7], ['cta', 8], ['footer', 9],
  ];
  const low = (symbol || '').toLowerCase();
  for (const [key, rank] of order) {
    if (low.includes(key)) return rank;
  }
  return 50;
}

function sortLandingSectionSymbols(names) {
  return [...names].sort((a, b) => {
    const diff = landingSectionRank(a) - landingSectionRank(b);
    return diff !== 0 ? diff : a.toLowerCase().localeCompare(b.toLowerCase());
  });
}

function isSectionLandingApp(code) {
  if (!code || isRouterShellApp(code) || isGenericScaffoldSource(code)) return false;
  const refs = code.match(/<\s*(Navbar|Hero|Footer|Features|Testimonials|CTA|StatsStrip|Header|Pricing|FAQ)\b/g) || [];
  return new Set(refs.map((r) => r.replace(/[<>\s]/g, ''))).size >= 2;
}

function findSectionLandingApp(files) {
  const hits = [];
  for (const [path, content] of Object.entries(files || {})) {
    const norm = path.replace(/\\/g, '/');
    if (!/^src\/App\.(jsx|tsx)$/i.test(norm) || !isSectionLandingApp(content)) continue;
    hits.push([norm.endsWith('.jsx') ? 0 : 1, path, content]);
  }
  hits.sort((a, b) => a[0] - b[0]);
  return hits.length ? [hits[0][1], hits[0][2]] : ['', ''];
}

function buildSyntheticIndexSource(candidates) {
  const names = [];
  for (const [path, content] of candidates) {
    const norm = path.replace(/\\/g, '/');
    if (!norm.includes('/components/') && !norm.includes('/pages/')) continue;
    if (isLibraryPackPreviewPath(path)) continue;
    if (isUtilityPreviewPage(path, content)) continue;
    const name = previewComponentSymbol(norm);
    if (!name || name === 'Index' || names.includes(name)) continue;
    names.push(name);
    if (names.length >= 12) break;
  }
  if (!names.length) return '';
  const ordered = sortLandingSectionSymbols(names);
  return `function Index() {
  return (
    <main className="min-h-screen">
${ordered.map((name) => `      <${name} />`).join('\n')}
    </main>
  );
}`;
}

function resolvePreviewRootFromFiles(files) {
  let indexContent = '';
  let indexFromPage = false;
  const { path: previewAppPath, content: appContent } = pickPreviewAppContent(files);
  let componentFiles = {};

  for (const [path, content] of Object.entries(files || {})) {
    if (!content) continue;
    const norm = path.replace(/\\/g, '/');
    if (path === previewAppPath) continue;
    if ((norm.includes('pages/Index') || norm.includes('pages/index')) && !isGenericScaffoldSource(content)) {
      indexContent = content;
      indexFromPage = true;
      componentFiles[path] = content;
    } else if (/\.(tsx|jsx|ts|js)$/.test(path) && path.includes('src/')) {
      trackPreviewComponentFile(componentFiles, path, content);
    }
  }

  let routerShellMode = isRouterShellApp(appContent) && Boolean(appContent);
  let skipPagesInPreview = indexFromPage && !routerShellMode;
  const scaffoldIndex = Object.entries(files || {}).find(([p]) => /pages\/Index\.(tsx|jsx)$/i.test(p.replace(/\\/g, '/')));

  if (routerShellMode) {
    indexContent = appContent;
    indexFromPage = false;
    componentFiles = {};
    for (const [path, content] of Object.entries(files || {})) {
      if (!content) continue;
      const norm = path.replace(/\\/g, '/');
      if (norm.includes('/pages/') && /\.(tsx|jsx)$/.test(path)) {
        trackPreviewComponentFile(componentFiles, path, content);
      } else if (/\.(tsx|jsx|ts|js)$/.test(path) && path.includes('src/') && !norm.includes('App.tsx') && !norm.includes('App.jsx')) {
        trackPreviewComponentFile(componentFiles, path, content);
      }
    }
  } else if (!indexContent && appContent && !isRouterShellApp(appContent) && !isGenericScaffoldSource(appContent)) {
    indexContent = appContent;
  }

  const indexPageIsScaffold = Boolean(scaffoldIndex && isGenericScaffoldSource(scaffoldIndex[1]));
  if (routerShellMode && (indexPageIsScaffold || !indexFromPage)) {
    const [landingPath, landingContent] = findSectionLandingApp(files);
    if (landingPath && landingContent) {
      routerShellMode = false;
      skipPagesInPreview = false;
      indexContent = landingContent;
      indexFromPage = false;
      componentFiles = {};
      for (const [path, content] of Object.entries(files || {})) {
        if (!content || path === landingPath) continue;
        const norm = path.replace(/\\/g, '/');
        if (norm.includes('App.tsx') || norm.includes('App.jsx')) continue;
        if (/pages\/Index\.(tsx|jsx)$/i.test(norm) && isGenericScaffoldSource(content)) continue;
        if (/\.(tsx|jsx|ts|js)$/.test(path) && path.includes('src/')) {
          trackPreviewComponentFile(componentFiles, path, content);
        }
      }
    } else {
    const candidates = findNonScaffoldPreviewCandidates(files);
    if (candidates.length) {
      routerShellMode = false;
      skipPagesInPreview = false;
      componentFiles = {};
      for (const [path, content] of Object.entries(files || {})) {
        if (!content) continue;
        const norm = path.replace(/\\/g, '/');
        if (norm.includes('App.tsx') || norm.includes('App.jsx')) continue;
        if (/pages\/Index\.(tsx|jsx)$/i.test(norm) && isGenericScaffoldSource(content)) continue;
        if (/\.(tsx|jsx|ts|js)$/.test(path) && path.includes('src/')) {
          trackPreviewComponentFile(componentFiles, path, content);
        }
      }
      const pageCandidate = candidates.find(([p, c]) => {
        const norm = p.replace(/\\/g, '/');
        return norm.includes('/pages/') && !isUtilityPreviewPage(p, c);
      });
      if (pageCandidate) {
        indexContent = pageCandidate[1];
        indexFromPage = true;
        delete componentFiles[pageCandidate[0]];
      } else if (candidates.length === 1) {
        indexContent = candidates[0][1];
        indexFromPage = candidates[0][0].replace(/\\/g, '/').includes('/pages/');
        delete componentFiles[candidates[0][0]];
      } else {
        const synthetic = buildSyntheticIndexSource(candidates);
        indexContent = synthetic || candidates[0][1];
        indexFromPage = true;
        if (!synthetic) delete componentFiles[candidates[0][0]];
      }
    }
    }
  }

  if (!indexContent) {
    const candidates = findNonScaffoldPreviewCandidates(files);
    if (candidates.length === 1) {
      indexContent = candidates[0][1];
      indexFromPage = candidates[0][0].replace(/\\/g, '/').includes('/pages/');
      delete componentFiles[candidates[0][0]];
    } else if (candidates.length > 1) {
      const synthetic = buildSyntheticIndexSource(candidates);
      if (synthetic) {
        indexContent = synthetic;
        indexFromPage = true;
      } else {
        indexContent = candidates[0][1];
        delete componentFiles[candidates[0][0]];
      }
    }
  }

  componentFiles = prunePreviewComponentFiles(componentFiles, { indexContent, indexFromPage });

  return {
    indexContent,
    indexFromPage,
    componentFiles,
    routerShellMode,
    skipPagesInPreview,
  };
}

function pickPreviewRootComponent(files) {
  const candidates = findNonScaffoldPreviewCandidates(files)
    .filter(([path, content]) => !isUtilityPreviewPage(path, content));
  if (!candidates.length) return null;
  const componentCandidates = candidates.filter(([p]) => p.replace(/\\/g, '/').includes('/components/'));
  if (componentCandidates.length >= 2) {
    const synthetic = buildSyntheticIndexSource(candidates);
    if (synthetic) return { path: 'src/pages/Index.tsx', content: synthetic };
  }
  const [path, content] = candidates[0];
  return { path: path.replace(/\\/g, '/'), content };
}

function ensureSandpackEntry(out, projectName) {
  const entry = resolveSandpackEntry(
    Object.fromEntries(Object.entries(out).map(([k, v]) => [k.replace(/^\//, ''), v.code])),
  );
  if (entry) return entry;
  if (out['/src/main.tsx'] || out['/src/main.jsx']) return out['/src/main.tsx'] ? '/src/main.tsx' : '/src/main.jsx';
  if (!out['/src/index.tsx'] && !out['/src/index.jsx'] && out['/src/App.tsx']) {
    out['/src/index.tsx'] = {
      code: `import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);`,
    };
    return '/src/index.tsx';
  }
  return '/src/index.tsx';
}

function resolveSandpackAppPath(out) {
  const jsx = out['/src/App.jsx']?.code || '';
  const tsx = out['/src/App.tsx']?.code || '';
  if (jsx && !isRouterShellApp(jsx) && !isSandpackDefaultApp(jsx)) return '/src/App.jsx';
  if (tsx && !isRouterShellApp(tsx) && !isSandpackDefaultApp(tsx)) return '/src/App.tsx';
  if (out['/src/App.jsx']) return '/src/App.jsx';
  if (out['/src/App.tsx']) return '/src/App.tsx';
  return null;
}

function wireSandpackAppRoot(out, files) {
  const appPath = resolveSandpackAppPath(out);
  const existingApp = appPath ? out[appPath]?.code : '';
  const appIsUsableInSandpack = existingApp
    && !isSandpackDefaultApp(existingApp)
    && !isRouterShellApp(existingApp);
  if (appPath && appIsUsableInSandpack) return;

  const root = pickPreviewRootComponent(files);
  if (!root) return;

  const compName = root.path.split('/').pop().replace(/\.(tsx|jsx)$/, '');
  const importPath = `./${root.path.replace(/^src\//, '').replace(/\.(tsx|jsx)$/, '')}`;
  const targetPath = appPath || '/src/App.tsx';
  out[targetPath] = {
    code: `import ${compName} from "${importPath}";

export default function App() {
  return <${compName} />;
}
`,
  };
}

const SANDPACK_EXTRA_DEPS = {
  '@radix-ui/react-slot': '^1.0.2',
  'class-variance-authority': '^0.7.0',
  clsx: '^2.1.0',
  'tailwind-merge': '^2.2.0',
  'lucide-react': '^0.400.0',
};

const SANDPACK_BUTTON_STUB = `import * as React from "react";

export function Button({ className = "", children, ...props }) {
  return (
    <button
      type="button"
      className={\`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-50 \${className}\`}
      {...props}
    >
      {children}
    </button>
  );
}

export const buttonVariants = () => "";
export default Button;
`;

const SANDPACK_UTILS_STUB = `import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}
`;

function stripImportMeta(code) {
  if (!code || typeof code !== 'string') return code;
  return code
    .replace(/import\.meta\.env\.[A-Z0-9_]+/g, '""')
    .replace(/import\.meta\.env/g, '({})')
    .replace(/import\.meta\.url/g, '""')
    .replace(/import\.meta\.dirname/g, '""')
    .replace(/import\.meta/g, '({})');
}

function isSandpackExcludedPath(path) {
  const norm = String(path || '').replace(/\\/g, '/').toLowerCase();
  return /(?:^|\/)(?:vite|vitest|tailwind|postcss|eslint|playwright)\.config\.(?:[cm]?[jt]s)$/.test(norm)
    || norm.endsWith('.d.ts')
    || norm.includes('/vite-env.d.ts');
}

function normalizeProjectPath(path) {
  return String(path || '').replace(/\\/g, '/').replace(/^\//, '');
}

function dirname(path) {
  const norm = normalizeProjectPath(path);
  const idx = norm.lastIndexOf('/');
  return idx >= 0 ? norm.slice(0, idx) : '';
}

function relativeImport(fromFile, targetFile) {
  const fromParts = dirname(fromFile).split('/').filter(Boolean);
  const targetParts = normalizeProjectPath(targetFile).split('/').filter(Boolean);
  const targetName = targetParts.pop();
  const targetDir = targetParts;

  let shared = 0;
  while (shared < fromParts.length && shared < targetDir.length && fromParts[shared] === targetDir[shared]) {
    shared += 1;
  }
  const ups = fromParts.length - shared;
  const down = targetDir.slice(shared);
  const segments = [...Array(ups).fill('..'), ...down, targetName].filter(Boolean);
  return segments.length ? `./${segments.join('/')}` : `./${targetName}`;
}

function resolveAliasTarget(subpath, fileKeys) {
  const base = `src/${subpath}`.replace(/\/$/, '');
  if (fileKeys.has(base)) return base;
  for (const ext of ['.tsx', '.ts', '.jsx', '.js']) {
    if (fileKeys.has(`${base}${ext}`)) return `${base}${ext}`;
  }
  for (const ext of ['.tsx', '.ts', '.jsx', '.js']) {
    if (fileKeys.has(`${base}/index${ext}`)) return `${base}/index${ext}`;
  }
  return `${base}.tsx`;
}

function rewriteAliasImports(code, currentFile, fileKeys) {
  if (!code || typeof code !== 'string') return code;
  return code
    .replace(/from\s+(['"])@shadcn\/ui\/([^'"]+)\1/g, (match, quote, name) => {
      const target = resolveAliasTarget(`components/ui/${name}`, fileKeys);
      return `from ${quote}${relativeImport(currentFile, target)}${quote}`;
    })
    .replace(/from\s+(['"])@\/([^'"]+)\1/g, (match, quote, subpath) => {
      const target = resolveAliasTarget(subpath, fileKeys);
      return `from ${quote}${relativeImport(currentFile, target)}${quote}`;
    });
}

/** Fix invalid @shadcn/ui package imports and @/ aliases for in-browser Sandpack. */
export function sanitizeSandpackFiles(files) {
  if (!files || typeof files !== 'object') return {};

  const out = { ...files };
  const fileKeys = new Set(Object.keys(out).map(normalizeProjectPath));

  for (const [path, content] of Object.entries(out)) {
    if (typeof content !== 'string' || !/\.(tsx?|jsx?)$/.test(path)) continue;
    let next = rewriteAliasImports(content, path, fileKeys);
    next = stripImportMeta(next);
    if (/@shadcn\/ui\//.test(next)) {
      if (path.replace(/\\/g, '/').endsWith('components/ui/button.tsx')) {
        next = SANDPACK_BUTTON_STUB;
      } else {
        next = next.replace(/@shadcn\/ui\/[^'"]+/g, '@/components/ui/button');
        next = rewriteAliasImports(next, path, fileKeys);
      }
    }
    out[path] = next;
  }

  const hasUtils = fileKeys.has('src/lib/utils.ts') || fileKeys.has('src/lib/utils.js');
  const referencesCn = Object.values(out).some(
    (content) => typeof content === 'string' && (content.includes('@/lib/utils') || content.includes('from "@/lib/utils"') || content.includes("from '@/lib/utils'")),
  );
  if (!hasUtils && referencesCn) {
    out['src/lib/utils.ts'] = SANDPACK_UTILS_STUB;
    fileKeys.add('src/lib/utils.ts');
  }

  for (const path of Object.keys(out)) {
    if (path.replace(/\\/g, '/').endsWith('components/ui/button.tsx') && typeof out[path] === 'string' && out[path].includes('@shadcn/ui')) {
      out[path] = SANDPACK_BUTTON_STUB;
    }
  }

  return out;
}

function mergeSandpackDependencies(pkgCode) {
  try {
    const pkg = JSON.parse(pkgCode || '{}');
    pkg.dependencies = { ...(pkg.dependencies || {}), ...SANDPACK_EXTRA_DEPS };
    return JSON.stringify(pkg, null, 2);
  } catch {
    return pkgCode;
  }
}

/** Decide which preview surface to show in the app builder. */
export function resolvePreviewEngine({
  instantPreviewReady = false,
  preferSandpack = false,
  sandpackRenderable = false,
  useE2bPreview = false,
  shouldPreferVitePreview = false,
  viteLive = false,
}) {
  const useVitePreview = !useE2bPreview && shouldPreferVitePreview && viteLive && !instantPreviewReady;
  const sandpackWanted = preferSandpack && sandpackRenderable;
  const useSandpack = !useE2bPreview && !shouldPreferVitePreview && sandpackWanted && !instantPreviewReady;
  const hasInstantPreview = !useE2bPreview && !useVitePreview && !useSandpack && instantPreviewReady;
  const bootingViteWithInstant = false;
  const vitePreviewLoading = !useE2bPreview && shouldPreferVitePreview && !viteLive && !instantPreviewReady && !useSandpack;
  return {
    useE2bPreview,
    useVitePreview,
    useSandpack,
    hasInstantPreview,
    bootingViteWithInstant,
    vitePreviewLoading,
  };
}

/** True when Sandpack has enough real source to render something. */
export function isSandpackRenderable(files, projectName = 'App') {
  if (!shouldUseSandpack(files)) return false;
  const sandpackFiles = filesToSandpack(files, projectName);
  const keys = Object.keys(sandpackFiles || {}).filter((k) => !k.startsWith('__'));
  if (keys.length < 2) return false;
  const appCode = sandpackFiles['/src/App.tsx']?.code || sandpackFiles['/src/App.jsx']?.code || '';
  const hasRealApp = appCode && !isSandpackDefaultApp(appCode);
  const hasPageFiles = Object.keys(files || {}).some((p) => {
    const norm = p.replace(/\\/g, '/');
    if (!norm.includes('/pages/')) return false;
    const content = files[p];
    return content && !isSandpackDefaultApp(content) && !isGenericScaffoldSource(content);
  });
  return hasRealApp || hasPageFiles;
}

/** True when generated files can produce an in-browser React preview. */
export function canBuildInstantReactPreview(files, projectName = 'App') {
  const html = buildReactPreviewFromFiles(files, projectName);
  return Boolean(html && html.length >= 200 && !isStaleReactPreviewHtml(html));
}

/** Convert project files dict to Sandpack files map. */
export function filesToSandpack(files, projectName = 'App') {
  if (!files || typeof files !== 'object') return {};

  const sanitized = sanitizeSandpackFiles(files);
  const out = {};
  for (const [path, content] of Object.entries(sanitized)) {
    if (!content || typeof content !== 'string') continue;
    const norm = path.replace(/\\/g, '/').replace(/^\//, '');
    if (norm === 'prompt' || norm.startsWith('.') || isSandpackExcludedPath(norm)) continue;
    out[`/${norm}`] = { code: content, hidden: norm.includes('node_modules') };
  }

  if (out['/package.json']) {
    out['/package.json'] = {
      ...out['/package.json'],
      code: mergeSandpackDependencies(out['/package.json'].code),
    };
  }

  if (!out['/package.json']) {
    const template = resolveSandpackTemplate(files);
    out['/package.json'] = {
      code: JSON.stringify({
        name: (projectName || 'app').toLowerCase().replace(/\s+/g, '-'),
        private: true,
        dependencies: {
          react: '^18.2.0',
          'react-dom': '^18.2.0',
          ...SANDPACK_EXTRA_DEPS,
          ...(template === 'react-ts' ? { typescript: '^5.0.0' } : {}),
        },
      }, null, 2),
    };
  }

  if (!out['/public/index.html'] && !out['/index.html']) {
    out['/public/index.html'] = {
      code: `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${String(projectName).replace(/[<>&"]/g, '')}</title>
    <script src="https://cdn.tailwindcss.com"></script>
  </head>
  <body><div id="root"></div></body>
</html>`,
    };
  }

  wireSandpackAppRoot(out, sanitized);
  ensureSandpackEntry(out, projectName);

  const entryCandidates = [
    '/src/main.tsx', '/src/main.jsx', '/src/index.tsx', '/src/index.jsx', '/src/App.tsx', '/src/App.jsx',
  ];
  const hasEntry = entryCandidates.some((p) => out[p]);
  if (!hasEntry) {
    const firstTsx = Object.keys(out).find((p) => /\.(tsx|jsx)$/.test(p) && p.includes('/src/'));
    if (firstTsx) {
      const comp = firstTsx.split('/').pop().replace(/\.(tsx|jsx)$/, '');
      out['/src/index.tsx'] = {
        code: `import React from "react";
import { createRoot } from "react-dom/client";
import ${comp} from ".${firstTsx.replace('/src', '')}";

createRoot(document.getElementById("root")!).render(<${comp} />);`,
      };
    }
  }

  return out;
}



/** In-browser React preview — mirrors backend _build_react_preview_html (no Vite/port). */
export function buildReactPreviewFromFiles(files, projectName = 'App') {
  if (!files || typeof files !== 'object') return '';

  const {
    indexContent,
    indexFromPage,
    componentFiles,
    skipPagesInPreview,
  } = resolvePreviewRootFromFiles(files);

  if (!indexContent) return '';

  const reservedNames = collectReservedPreviewNames(componentFiles, skipPagesInPreview);
  const lucideNeeded = collectLucideIconsNeeded(componentFiles, indexContent, skipPagesInPreview);
  const lucideStubs = buildLucideStubs(reservedNames, lucideNeeded);
  const wantedAnchors = collectPreviewAnchorHrefs(files);
  const { sectionsJs: anchorSectionsJs, renderBlock: anchorRenderBlock } = buildMissingAnchorPreviewJs(files);

  let allComponentsCode = '';
  for (const [path, content] of Object.entries(componentFiles).sort(([a], [b]) => a.localeCompare(b))) {
    if (!/\.(tsx|jsx)$/.test(path) || !content) continue;
    if (shouldSkipPreviewComponent(path)) continue;
    const norm = path.replace(/\\/g, '/');
    if (skipPagesInPreview && norm.includes('/pages/')) continue;
    let body = preparePreviewSource(content, reservedNames);
    body = injectSectionIdForAnchor(body, path, wantedAnchors);
    body = wrapPreviewComponentIife(body, previewComponentSymbol(path));
    allComponentsCode += `\n// --- ${path} ---\n${body}\n`;
  }

  const indexBody = preparePreviewSource(indexContent, reservedNames);
  const definedNames = new Set([...KNOWN_PREVIEW_GLOBALS, ...reservedNames, ...lucideNeeded]);
  const autoStubs = autoPassthroughStubs([indexBody, allComponentsCode], definedNames);
  const safeTitle = String(projectName).replace(/[<>&"]/g, '');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<title>${safeTitle}</title>
<script>(function(){var w=console.warn;console.warn=function(){var m=String(arguments[0]||"");if(m.indexOf("cdn.tailwindcss.com")>=0||m.indexOf("in-browser Babel")>=0)return;w.apply(console,arguments);};})();</script>
<script>
window.addEventListener('error', function(e) {
  var root = document.getElementById('root');
  if (!root || root.childNodes.length) return;
  var msg = (e && e.message) ? e.message : 'Preview failed to compile';
  if (msg === 'Script error.' && e && e.error && e.error.message) msg = e.error.message;
  root.innerHTML = '<div style="padding:2rem;font-family:system-ui,sans-serif;text-align:center"><p style="color:#dc2626;font-weight:600;margin:0 0 0.5rem">Preview compile error</p><p style="color:#71717a;font-size:12px;margin:0">' + msg.replace(/</g,'&lt;') + '</p></div>';
});
</script>
<script src="https://cdn.tailwindcss.com"></script>
<script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
<script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
<script>
(function () {
  if (!window.Babel || !Babel.registerPreset) return;
  Babel.registerPreset('snagcart-preview-react', {
    presets: [[Babel.availablePresets.react, { runtime: 'classic', development: false }]],
  });
})();
</script>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap"/>
<style>
body { font-family: 'Inter', sans-serif; margin: 0; }
* { box-sizing: border-box; }
</style>
</head>
<body>
<div id="root"></div>
<script type="text/babel" data-presets="snagcart-preview-react">
const Button = ({children, className = "", ...props}) => (
  <button className={"inline-flex items-center justify-center rounded-md text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 h-10 px-4 py-2 " + className} {...props}>{children}</button>
);
const Card = ({children, className = "", ...props}) => <div className={"rounded-lg border bg-white text-zinc-900 shadow-sm " + className} {...props}>{children}</div>;
const CardHeader = ({children, className = "", ...props}) => <div className={"flex flex-col space-y-1.5 p-6 " + className} {...props}>{children}</div>;
const CardContent = ({children, className = "", ...props}) => <div className={"p-6 pt-0 " + className} {...props}>{children}</div>;
const CardTitle = ({children, className = "", ...props}) => <h3 className={"text-2xl font-semibold leading-none tracking-tight " + className} {...props}>{children}</h3>;
const CardDescription = ({children, className = "", ...props}) => <p className={"text-sm text-zinc-500 " + className} {...props}>{children}</p>;
const CardFooter = ({children, className = "", ...props}) => <div className={"flex items-center p-6 pt-0 " + className} {...props}>{children}</div>;
const Badge = ({children, className = "", ...props}) => <span className={"inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold " + className} {...props}>{children}</span>;
const Input = ({className = "", ...props}) => <input className={"flex h-10 w-full rounded-md border px-3 py-2 text-sm " + className} {...props} />;
const Textarea = ({className = "", ...props}) => <textarea className={"flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm " + className} {...props} />;
const Separator = ({className = ""}) => <hr className={"shrink-0 border-t " + className} />;
const Avatar = ({children, className = ""}) => <span className={"relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full " + className}>{children}</span>;
const Tabs = ({children, className = "", ...props}) => <div className={className} {...props}>{children}</div>;
const TabsList = ({children, className = ""}) => <div className={"inline-flex h-10 items-center justify-center rounded-md bg-zinc-100 p-1 " + className}>{children}</div>;
const TabsTrigger = ({children, className = "", ...props}) => <button className={"inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium " + className} {...props}>{children}</button>;
const TabsContent = ({children, className = "", ...props}) => <div className={className} {...props}>{children}</div>;
const NavigationMenu = ({children, className = ""}) => <nav className={className}>{children}</nav>;
const Accordion = ({children, className = "", ...props}) => <div className={className} {...props}>{children}</div>;
const AccordionItem = ({children, className = "", title, ...props}) => <div className={"border-b " + className} {...props}>{title ? <div className="py-4 font-medium">{title}</div> : null}{children}</div>;
const AccordionTrigger = ({children, className = "", ...props}) => <div className={"flex w-full items-center justify-between py-4 font-medium " + className} {...props}>{children}</div>;
const AccordionContent = ({children, className = "", ...props}) => <div className={"pb-4 text-zinc-600 " + className} {...props}>{children}</div>;
Accordion.Item = AccordionItem;
Accordion.Trigger = AccordionTrigger;
Accordion.Header = AccordionTrigger;
Accordion.Content = AccordionContent;
const Dialog = ({children}) => <div>{children}</div>;
const Label = ({children, className = "", ...props}) => <label className={"text-sm font-medium " + className} {...props}>{children}</label>;
const LucideIcon = ({className = "", size = 24}) => <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>;
const CheckIcon = ({className = "", size = 24}) => <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20,6 9,17 4,12"/></svg>;
${FRAMER_MOTION_STUB}
${lucideStubs}
${REACT_PREVIEW_PROVIDER_STUBS}
${autoStubs}
${allComponentsCode}
${indexBody}
${anchorSectionsJs}
const AppComponent = typeof App !== 'undefined' ? App : (typeof Index !== 'undefined' ? Index : () => <div className="p-8 text-center text-zinc-500">Loading preview…</div>);
const root = ReactDOM.createRoot(document.getElementById('root'));
try {
  root.render(${anchorRenderBlock});
} catch (previewErr) {
  root.render(
    <div className="p-8 text-center">
      <p className="text-sm font-semibold text-red-600">Preview could not render</p>
      <p className="mt-2 text-xs text-zinc-500">{String(previewErr && previewErr.message ? previewErr.message : previewErr)}</p>
    </div>
  );
}
${PREVIEW_ANCHOR_NAV_BOOTSTRAP}
</script>
</body>
</html>`;
}



export function isStaleReactPreviewHtml(html) {
  if (!html || typeof html !== 'string') return true;
  if (!html.includes('text/babel')) return false;
  if (html.includes('data-type="module"') || html.includes("data-type='module'")) return true;
  if (html.includes('react/jsx-runtime') || html.includes('react/jsx-dev-runtime')) return true;
  const script = html.split('type="text/babel"')[1]?.split('</script>')[0] || html;
  if (/welcome to snagcart/i.test(html) && /start building your amazing project here/i.test(html)) {
    // Ignore unused scaffold Index text when the preview root renders real landing sections.
    if (/function App\s*\([\s\S]*<(?:Navbar|Hero|HeroSection)\b/.test(script)) return false;
    if (/<(?:Navbar|Hero|HeroSection)\b/.test(script) && !/element=\{<Index/.test(script)) return false;
    return true;
  }
  if (/\/\/ --- src\/App\.(tsx|jsx) ---/.test(script)) return true;
  if (script.includes('</Routes>') && !script.includes('<Routes>')) return true;
  if (/const Route = \(\) => null/.test(script)) return true;
  if (script.includes('HashRouter') && !script.includes('__HashRouterInner')) return true;
  if ((script.match(/^export\s+default\s+/gm) || []).length > 1) {
    return true;
  }
  if (/const\s+MadeWithOpen-Lovable/.test(script)) {
    return true;
  }
  if (/components\/Menu\.tsx/.test(script) && /const Menu = LucideIcon/.test(script)) {
    return true;
  }
  if (/components\/Menu\.(tsx|jsx)/.test(script) && /components\/Navbar\.(tsx|jsx)/.test(script)
    && /<Menu\s+size\s*=/.test(script) && !/__LucideMenu/.test(script)) {
    return true;
  }
  return false;
}



/** Resolve self-contained HTML for iframe srcdoc (never needs Vite). */
export function resolvePreviewDocument(project) {
  const files = project?.files;
  const projectName = project?.name || 'App';
  const stored = (project?.preview_html || '').trim();
  const reactHtml = buildReactPreviewFromFiles(files, projectName);
  const fromFiles = buildPreviewFromFiles(files);

  // Prefer an explicit generated preview.html/index.html artifact before
  // rebuilding React in-browser. Some agent outputs include a polished static
  // preview.html plus a partially-generated React/Vite tree; rebuilding the
  // React tree can fail at runtime and leave the iframe stuck on "Loading".
  if (fromFiles && fromFiles.length > 200 && !isViteReactScaffold(fromFiles)) return fromFiles;
  if (stored && !isViteReactScaffold(stored) && !isStaleReactPreviewHtml(stored) && !isGenericScaffoldSource(stored)) return stored;
  if (reactHtml && reactHtml.length > 500) return reactHtml;
  return reactHtml || (stored && !isStaleReactPreviewHtml(stored) ? stored : '') || fromFiles || '';
}



/**

 * Single source of truth for preview iframe — prefer catalog HTML with inlined CSS.

 */

export function deriveLivePreview(project) {

  const files = project?.files;

  const rawIndex = files?.['index.html'] || files?.['preview.html'] || '';

  const stored = project?.preview_html || '';

  const mode = project?.metadata?.generation_mode;

  const agentMode = mode === 'openlovable-agent' || mode === 'openlovable-llm-only' || mode === 'siteforge';

  const projectName = project?.name || 'App';

  const reactHtml = buildReactPreviewFromFiles(files, projectName);

  const fromFiles = buildPreviewFromFiles(files);

  // Prefer explicit generated preview artifacts before synthetic React rebuilds.
  // This avoids blank previews when a full Vite tree has compile/runtime issues
  // but the builder also produced a valid, polished preview.html artifact.
  if (fromFiles && fromFiles.length > 200 && !isViteReactScaffold(fromFiles)) return fromFiles;

  if (reactHtml && reactHtml.length > 500) return reactHtml;

  if (agentMode || hasReactWorkspace(files)) {

    if (stored && !isViteReactScaffold(stored) && !isCatalogHtml(stored) && !isStaleReactPreviewHtml(stored) && !(reactHtml && reactHtml.length > 500)) return stored;

    if (fromFiles && !isViteReactScaffold(fromFiles) && !isCatalogHtml(fromFiles)) return fromFiles;

    return reactHtml || (stored && !isViteReactScaffold(stored) && !isStaleReactPreviewHtml(stored) ? stored : '');

  }



  // Never flash deterministic catalog scaffolds — wait for AI-authored output.
  const buildingStates = ['planning', 'generating', 'testing', 'deploying', 'design_review', 'awaiting_requirements', 'awaiting_clarification'];
  const isBuilding = buildingStates.includes(project?.state);

  if (fromFiles && isCatalogHtml(fromFiles) && (isBuilding || project?.metadata?.generation_mode !== 'phase1-catalog')) {
    return '';
  }

  if (stored && isCatalogHtml(stored) && (isBuilding || project?.metadata?.generation_mode !== 'phase1-catalog')) {
    return '';
  }

  // Catalog path: files or stored preview with SB-SECTION markers (phase1-catalog only)



  // Reject legacy LLM HTML (bullet nav) when catalog mode is active

  const catalogMode = project?.metadata?.generation_mode === 'phase1-catalog';

  if (catalogMode) {

    if (fromFiles && !isLegacyLlmHtml(fromFiles) && !isScaffoldIndexHtml(rawIndex)) {

      return fromFiles;

    }

    if (stored && !isLegacyLlmHtml(stored) && !isScaffoldIndexHtml(stored)) {

      return stored;

    }

    return '';

  }



  if (fromFiles && !isScaffoldIndexHtml(rawIndex)) {

    return fromFiles;

  }

  if (stored && !isScaffoldIndexHtml(stored)) {

    return stored;

  }



  return '';

}



export function mergeProjectPreview(project, patch = {}) {

  const merged = { ...project, ...patch };

  if (patch.files != null) {

    merged.files = patch.files;

  }

  const rebuilt = deriveLivePreview(merged);
  if (rebuilt && rebuilt.length > 500) {
    return { ...merged, preview_html: rebuilt };
  }

  if (typeof patch.preview_html === 'string' && patch.preview_html) {
    if (
      !isStaleReactPreviewHtml(patch.preview_html)
      && !isCatalogHtml(patch.preview_html)
      && !isViteReactScaffold(patch.preview_html)
    ) {
      merged.preview_html = patch.preview_html;
      return merged;
    }
  }

  return {

    ...merged,

    preview_html: rebuilt || deriveLivePreview(merged),

  };

}



